<script lang="ts" setup>
import { StepsController } from '@/controllers/StepsController.ts'
import appStore from '@/store/AppStore.ts'
import bus from '@/helper/Bus.ts'
import { BusEvent } from '@/helper/Types.ts'
import { onMounted, onUnmounted, ref } from 'vue'
import { isTranslationPresent as isTranslationPresent } from '@/helper/Translations.ts'

const COMPONENT_NAME = 'StepsControls'
const scrolledToBottom = ref(false)

onMounted((): void => {
  window.addEventListener('scroll', handleScroll)
  handleScrollDelayed()
  bus.$onC(COMPONENT_NAME, BusEvent.STEP_CHANGED, handleScrollDelayed)
})

onUnmounted((): void => {
  window.removeEventListener('scroll', handleScroll)
  bus.$offC(COMPONENT_NAME)
})

async function clickNext(): Promise<void> {
  await StepsController.nextClicked()
}

async function clickBack(): Promise<void> {
  await StepsController.backClicked()
}

function handleScroll(): void {
  scrolledToBottom.value =
    Math.max(window.scrollY, document.documentElement.scrollTop, document.body.scrollTop) + window.innerHeight >=
    document.documentElement.offsetHeight
}

function handleScrollDelayed(): void {
  window.setTimeout(() => {
    handleScroll()
  }, 50)
}
</script>

<template>
  <div :class="{ 'has--shadow': !scrolledToBottom }" class="steps__controls controls">
    <div class="container">
      <slot name="back-button">
        <button
          v-if="!(appStore.firstStep === appStore.currentStep)"
          :disabled="appStore.isLoading"
          class="btn is--secondary controls__back"
          @click="clickBack"
        >
          <template v-if="isTranslationPresent('global.btn_back_template')">
            {{ $t('global.btn_back_template', { stepName: appStore.getStepName(appStore.prevStep) }) }}
          </template>
          <template v-else>
            {{ $t('global.btn_back') }} {{ appStore.prevStepName }}
          </template>
        </button>
      </slot>

      <slot name="next-button">
        <button
          :disabled="appStore.isLoading || !(appStore.currentFormValid || appStore.currentStepValid)"
          class="btn controls__next"
          @click="clickNext"
        >
          <template v-if="appStore.isCurrentStepTheLastStep">
            {{ $t('global.btn_next') }} {{ $t('nextstep.download_configuration') }}
          </template>

          <template v-else-if="isTranslationPresent('global.btn_next_template')">
            {{ $t('global.btn_next_template', { stepName: appStore.getStepName(appStore.nextStep) }) }}
          </template>

          <template v-else>
            {{ $t('global.btn_next') }} {{ appStore.nextStepName }}
          </template>
        </button>
      </slot>
    </div>
  </div>
</template>

<style lang="scss">
@import '@/assets/style/helpers/include-media';

.steps__controls {
  margin-right: calc(100% - 100vw); //Fixing scrollbar jump issue
  z-index: 3;
  position: fixed;
  background: #fff;
  right: 0;
  bottom: 0;
  left: 0;
  padding-top: 1rem;
  padding-bottom: 1rem;
  transition: box-shadow $animTimeBasic ease;

  &.has--shadow {
    box-shadow: 0 rem-calc(-2px) rem-calc(4px) rgba(0, 0, 0, 0.07);
  }

  .container {
    display: flex;
    @include media('<tablet') {
      gap: 1rem;
    }
  }

  @include media('<tablet') {
    .btn {
      text-overflow: ellipsis;
      overflow: hidden;
      height: rem-calc(40);
      white-space: nowrap;
      display: block;
      min-width: auto;
    }
  }

  .controls {
    &__next {
      margin-right: 0;
      margin-left: auto;
    }

    &__back {
      margin-right: auto;

      &.is--right {
        margin-right: rem-calc(16);
        margin-left: auto;
      }
    }
  }
}
</style>
